import React, { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AddVehicleForm Component
 * User-friendly form for vehicle registration requests with owner contact information
 * Sends email notification to administrator for GPS device coordination
 */
const AddVehicleForm = ({ onClose, onSubmit }) => {
  // Vehicle details state
  const [vehicleData, setVehicleData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    license_plate: '',
    is_public: false,
    vehicle_type: 'private',
    update_frequency: 60
  });

  // Owner contact information state
  const [ownerData, setOwnerData] = useState({
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    owner_address: ''
  });

  // const StopSwipeBubbleForm = ({ children }) => {
  // const haltStart = (e) => e.stopPropagation();
  // return (
  //   <div
  //     // className="w-full h-full" 
  //     onTouchStart={haltStart}
  //     onPointerDown={haltStart}
  //   >
  //     {children}
  //   </div>
  // );
  // };

  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Common vehicle makes for dropdown
  const vehicleMakes = [
    'Toyota', 'Honda', 'Ford', 'Chevrolet', 'Nissan', 'BMW', 'Mercedes-Benz',
    'Audi', 'Volkswagen', 'Hyundai', 'Kia', 'Mazda', 'Subaru', 'Lexus',
    'Acura', 'Infiniti', 'Cadillac', 'Lincoln', 'Jeep', 'Dodge',
    'Chrysler', 'Buick', 'GMC', 'Ram', 'Volvo', 'Jaguar', 'Land Rover', 'Other'
  ];

  // Common colors for dropdown
  const colors = [
    'White', 'Black', 'Silver', 'Gray', 'Red', 'Blue', 'Green', 'Yellow',
    'Orange', 'Purple', 'Brown', 'Gold', 'Beige', 'Pink', 'Other'
  ];

  const handleVehicleChange = (field, value) => {
    setVehicleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOwnerChange = (field, value) => {
    setOwnerData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    if (!vehicleData.make || !vehicleData.model || !vehicleData.license_plate || !vehicleData.year) {
      setError('Please fill in all vehicle details');
      setLoading(false);
      return;
    }

    if (!ownerData.owner_name || !ownerData.owner_phone || !ownerData.owner_email) {
      setError('Please fill in all contact information');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(ownerData.owner_email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const requestData = {
        ...vehicleData,
        ...ownerData
      };

      const response = await axios.post(`${API}/vehicles/request`, requestData);

      if (response.data.status === 'success') {
        setSuccess(true);
        setTimeout(() => {
          onSubmit();
        }, 2000);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to submit vehicle request');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-green-600 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Request Submitted!</h2>
            <p className="text-gray-600 mb-4">
              Your vehicle registration request has been sent to the administrator.
              You will be contacted about GPS device purchase and setup.
            </p>
            <div className="bg-blue-50 border border-blue-200 p-3 rounded text-sm text-blue-800">
              <strong>Next Steps:</strong><br />
              • Administrator will review your request<br />
              • You'll receive a call/email about GPS device options<br />
              • Device setup will be coordinated after purchase
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">Add New Vehicle</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vehicle Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
              🚗 Vehicle Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Make *
                </label>
                <select
                  value={vehicleData.make}
                  onChange={(e) => handleVehicleChange('make', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Make</option>
                  {vehicleMakes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Model *
                </label>
                <input
                  type="text"
                  value={vehicleData.model}
                  onChange={(e) => handleVehicleChange('model', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Camry, F-150, Civic"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  value={vehicleData.year}
                  onChange={(e) => handleVehicleChange('year', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Color
                </label>
                <select
                  value={vehicleData.color}
                  onChange={(e) => handleVehicleChange('color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Color</option>
                  {colors.map(color => (
                    <option key={color} value={color}>{color}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  License Plate *
                </label>
                <input
                  type="text"
                  value={vehicleData.license_plate}
                  onChange={(e) => handleVehicleChange('license_plate', e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., ABC-123"
                  required
                />
              </div>
            </div>

            {/* Vehicle Options */}
            <div className="mt-4 space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={vehicleData.is_public}
                  onChange={(e) => handleVehicleChange('is_public', e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_public" className="ml-2 block text-sm text-gray-700">
                  Make this vehicle publicly visible (others can see its location)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vehicle Type
                </label>
                <select
                  value={vehicleData.vehicle_type}
                  onChange={(e) => handleVehicleChange('vehicle_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="private">Private Vehicle</option>
                  <option value="taxi">Taxi</option>
                  <option value="delivery">Delivery Vehicle</option>
                  <option value="bus">Bus</option>
                  <option value="truck">Truck</option>
                  <option value="van">Van</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GPS Update Frequency (seconds)
                </label>
                <select
                  value={vehicleData.update_frequency}
                  onChange={(e) => handleVehicleChange('update_frequency', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={30}>30 seconds (High frequency)</option>
                  <option value={60}>1 minute (Standard)</option>
                  <option value={120}>2 minutes (Low frequency)</option>
                  <option value={300}>5 minutes (Very low frequency)</option>
                </select>
              </div> */}
            </div>
          </div>

          {/* Owner Contact Information Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
              👤 Contact Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={ownerData.owner_name}
                  onChange={(e) => handleOwnerChange('owner_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={ownerData.owner_phone}
                  onChange={(e) => handleOwnerChange('owner_phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., +1-555-123-4567"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={ownerData.owner_email}
                  onChange={(e) => handleOwnerChange('owner_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address (Optional)
                </label>
                <input
                  type="text"
                  value={ownerData.owner_address}
                  onChange={(e) => handleOwnerChange('owner_address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Street address, City, State"
                />
              </div>
            </div>
          </div>

          {/* Information Box */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">📋 What happens next?</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Your request will be sent to our administrator</li>
              <li>• You'll be contacted within 24 hours about GPS device options</li>
              <li>• GPS devices are $50 with a $25/month subscription plan</li>
              <li>• Professional installation can be arranged if needed</li>
              <li>• Your vehicle will appear on the tracking system once activated</li>
            </ul>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Request'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddVehicleForm;